import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Star } from 'lucide-react';
import {
  Box,
  Typography,
  Collapse,
  IconButton,
  Tooltip
} from "@mui/material";
import { softColors } from "../../../styles/purchaseInvoices/pages/commonTableStyles.js";

const AccountantsList = ({ accountants = [], onChangePrimary, editable = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!accountants || accountants.length === 0) {
    return <Typography variant="body2">אין מנהלות חשבונות</Typography>;
  }

  // Get primary accountant
  const primaryAccountant = accountants.find(acc => acc.isPrimary) || accountants[0];
  const otherAccountants = accountants.filter(acc => 
    (acc._id && acc._id !== primaryAccountant._id) || 
    (!acc._id && acc.accountant !== primaryAccountant.accountant)
  );

  const handleSetPrimary = (accountant) => {
    if (editable && onChangePrimary) {
      onChangePrimary(accountant);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Primary Accountant - Always Visible */}
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          cursor: otherAccountants.length > 0 ? 'pointer' : 'default',
          '&:hover': otherAccountants.length > 0 ? { backgroundColor: 'rgba(0, 0, 0, 0.02)' } : {},
          padding: '4px 8px',
          borderRadius: '4px',
          transition: 'background-color 0.2s'
        }}
        onClick={() => otherAccountants.length > 0 && setIsExpanded(!isExpanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
          <Tooltip title="מנהלת חשבונות ראשית" placement="top">
            <span>
              <Star size={16} fill={softColors.primary} color={softColors.primary} />
            </span>
          </Tooltip>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {primaryAccountant.name || primaryAccountant.accountant}
          </Typography>
        </Box>
        {otherAccountants.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              +{otherAccountants.length}
            </Typography>
            <IconButton size="small" sx={{ padding: '2px' }}>
              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Other Accountants - Collapsible */}
      {otherAccountants.length > 0 && (
        <Collapse in={isExpanded} timeout="auto">
          <Box 
            sx={{ 
              mt: 1, 
              pl: 3,
              borderRight: `2px solid ${softColors.primary}20`,
              display: 'flex',
              flexDirection: 'column',
              gap: 1
            }}
          >
            {otherAccountants.map((acc, index) => (
              <Box 
                key={acc._id || index}
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  padding: '4px 8px',
                }}
              >
                {editable && onChangePrimary ? (
                  <Tooltip title="לחץ להגדיר כמנהלת ראשית" placement="right">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSetPrimary(acc);
                      }}
                      sx={{ 
                        padding: '2px',
                        color: 'grey.400',
                        '&:hover': { color: softColors.primary }
                      }}
                    >
                      <Star size={14} />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Box sx={{ width: '14px' }} />
                )}
                <Typography variant="body2" color="text.secondary">
                  {acc.name || acc.accountant}
                </Typography>
              </Box>
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

export default AccountantsList;
